import { useWindowDimensions } from 'tamagui'
import { Slider } from '@components/Slider'
import { useCallback, useMemo, useState } from 'react'
import { ProductCard } from '@components/Home/MyProducts/ProductCard'
import { ICourse } from '@dtos/Course'
import { CARD_DIMENSIONS } from '@constants/productCardDimension'
import { useMediaSize } from '@hooks/useMediaSize'
import { IEnrollment } from '@dtos/Enrollment'
import { useGetGeneralConfig } from '@hooks/useMembersConfig'
import { DefaultSectionTitle } from '@components/Home/DefaultSectionTitle'
import { EnrollmentContentsModal } from '@components/Home/EnrollmentContentsModal'
import { InView } from 'react-native-intersection-observer'
import { ContinueCard } from '@components/Home/Continue/ContinueCard'

interface IPathProductsSectionProps {
  enrollmentId: string
  products: ICourse[] | null
  enrollment: IEnrollment
  onLoadMore: () => void
}

export function PathProductsSection({
  enrollmentId,
  products,
  enrollment,
  onLoadMore,
}: IPathProductsSectionProps) {
  const [openModal, setOpenModal] = useState(false)
  const sizes = useMediaSize()
  const { width: windowWidth } = useWindowDimensions()
  const { data: generalConfig } = useGetGeneralConfig()
  const isHorizontal = true
  const showProductsName = generalConfig?.showProductName ?? true
  const containerPadding = 16
  const spaceBetweenCards = 16

  const findProgressCard = useCallback(
    (productId: number) => {
      return enrollment.progressCard?.courseProgressCards?.find(
        (course) => course.courseId === productId,
      )
    },
    [enrollment.progressCard?.courseProgressCards],
  )

  const slidesPerView = useMemo(() => {
    const orientation = isHorizontal ? 'horizontal' : 'vertical'
    const cardWidth =
      CARD_DIMENSIONS[orientation][sizes] || CARD_DIMENSIONS[orientation].xs
    return (
      (windowWidth - containerPadding) / (cardWidth.width + spaceBetweenCards)
    )
  }, [isHorizontal, sizes, windowWidth])

  const shouldDisplaySeeAllButton = useMemo(() => {
    return products && products?.length > slidesPerView
  }, [products, slidesPerView])

  const handleTitleButtonPress = () => {
    setOpenModal(true)
  }

  const renderModalCards = useMemo(() => {
    if (!products) return []
    return products?.map((product, index) => {
      if (index === products.length - 1) {
        return (
          <InView
            key={product.id}
            onChange={(inView: boolean) => inView && onLoadMore()}
            threshold={100}
          >
            <ContinueCard
              cardId={product.id.toString()}
              showProductName={showProductsName}
              shouldExtendCard={false}
              productTitle={product.name}
              isModalVersion={true}
              customThumbnail={product.imageUrl}
              displayPlayIcon={false}
              displayBuyTag={false}
              progressCard={findProgressCard(product.id)}
              usedAsProduct={true}
              course={product}
              firstLesson={
                product.courseModules?.nodes[0].courseContents.nodes[0]
                  .lesson ?? undefined
              }
              closeModal={() => setOpenModal(false)}
              enrollmentId={enrollmentId}
            />
          </InView>
        )
      } else {
        return (
          <ContinueCard
            cardId={product.id.toString()}
            showProductName={showProductsName}
            key={product.id.toString()}
            shouldExtendCard={false}
            productTitle={product.name}
            isModalVersion={true}
            customThumbnail={product.imageUrl}
            displayPlayIcon={false}
            displayBuyTag={false}
            progressCard={findProgressCard(product.id)}
            usedAsProduct={true}
            course={product}
            firstLesson={
              product.courseModules?.nodes[0].courseContents.nodes[0].lesson ??
              undefined
            }
            closeModal={() => setOpenModal(false)}
            enrollmentId={enrollmentId}
          />
        )
      }
    })
  }, [products, findProgressCard, showProductsName, enrollmentId, onLoadMore])

  if (products === null) return null
  return (
    <>
      <Slider
        slidesTotalNumber={products.length}
        slidesSpacing={spaceBetweenCards}
        slidesPerView={slidesPerView}
        loadMoreContent={onLoadMore}
        sliderTitle={
          <DefaultSectionTitle
            title="Produtos"
            isHoveringSection={false}
            onButtonPress={handleTitleButtonPress}
            showButton={shouldDisplaySeeAllButton ?? false}
          />
        }
        displaySliderControls={false}
      >
        {products.map((product) => {
          const progressCard = findProgressCard(product.id)
          return (
            <ProductCard
              key={product.id}
              hasAuxiliarTitle={showProductsName}
              isHorizontal={isHorizontal}
              data={{
                id: product.id.toString(),
                enrollmentId,
                courseId: product.id.toString(),
                title: product.name,
                logo: product.imageUrl,
                progress: progressCard?.progress,
                totalLessons: progressCard?.total,
                completedLessons: progressCard?.completed,
                kind: product.kind,
                courseContentId:
                  product.courseModules?.nodes?.[0]?.courseContents?.nodes?.[0]
                    ?.id ?? null,
                firstLesson:
                  product.courseModules?.nodes?.[0]?.courseContents?.nodes?.[0]
                    ?.lesson ?? null,
              }}
            />
          )
        })}
      </Slider>
      {openModal && (
        <EnrollmentContentsModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          lessonCards={renderModalCards}
          title={'Produtos'}
        />
      )}
    </>
  )
}
